import type { Layout } from "@motain/xpa-proto-files-web/lib/runtime/xpa";
import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { fetchXpaLayoutWithResponseInfo } from "@/utils/fetchXpaLayout";
import type { FC } from "react";
import type { XpaSupportedComponentsResolverProps } from "@/components/xpa-layout/XpaLayout";
import { XpaLayout } from "@/components/xpa-layout/XpaLayout";
import { Title } from "@/xpa-components/title/Title";
import styles from "@/styles/pages/Matches.module.scss";
import { HorizontalSeparator } from "@/xpa-components/horizontal-separator/HorizontalSeparator";
import { XpaGoogleAdsPlaceholder } from "@/modules/ads";
import { UpcomingMatches } from "@/xpa-components/upcoming-matches/UpcomingMatches";
import { MatchGallery } from "@/xpa-components/match-gallery/MatchGallery";
import { SectionHeader } from "@/xpa-components/section-header/SectionHeader";
import { MatchCardsList } from "@/xpa-components/match-cards-list/MatchCardsList";
import { EmptyState } from "@/xpa-components/empty-state/EmptyState";
import { ScrollToTopButton } from "@/components/scroll-to-top-button/ScrollToTopButton";
import {
  StreamName,
  useRegisterStream,
  useRegisterXpaStream,
} from "@/modules/analytics";
import { MatchTypesFilters } from "@/xpa-components/match-types-filters/MatchTypesFilters";

const CONTAINER_COUNT_LIMIT = 10;

const Matches: FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props,
) => {
  useRegisterStream(StreamName.MatchesStream);
  useRegisterXpaStream(props);

  return (
    <div className={styles.xpaContainer}>
      <XpaLayout
        layout={props}
        XpaSupportedComponentsResolver={XpaSupportedComponentsResolver}
        containersCountLimit={CONTAINER_COUNT_LIMIT}
      />
      <ScrollToTopButton className={styles.scrollToTopButton} />
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<Layout> = async (ctx) => {
  const layoutResponse = await fetchXpaLayoutWithResponseInfo(ctx);

  if (layoutResponse.status === 404) {
    return { notFound: true };
  } else {
    return { props: layoutResponse.layout };
  }
};

export default Matches;

export const XpaSupportedComponentsResolver: XpaSupportedComponentsResolverProps =
  ({ contentType }) => {
    switch (contentType.$case) {
      case "matchesFiltersComponent":
        return <MatchTypesFilters {...contentType.matchesFiltersComponent} />;
      case "sectionHeader":
        return <SectionHeader {...contentType.sectionHeader} />;
      case "horizontalSeparator":
        return <HorizontalSeparator {...contentType.horizontalSeparator} />;
      case "matchCardsList":
        return <MatchCardsList {...contentType.matchCardsList} />;
      case "matchGallery":
        return <MatchGallery {...contentType.matchGallery} />;
      case "upcomingMatches":
        return <UpcomingMatches {...contentType.upcomingMatches} />;
      case "emptyState":
        return <EmptyState {...contentType.emptyState} />;
      case "title":
        return <Title {...contentType.title} />;
      case "googleAdsPlaceholder":
        return (
          <XpaGoogleAdsPlaceholder {...contentType.googleAdsPlaceholder} />
        );
      default:
        return null;
    }
  };
